import { useEffect } from 'react';
import { syncStoredSession } from '@/utils/authStorage.js';

const QA_ENABLED =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_OPERATOR_COMPACT_QA === '1';
const QA_SESSION_ENABLED =
  import.meta.env.DEV && import.meta.env.VITE_ENABLE_QA_SESSION_INJECTION === '1';

function parseJsonEnv(value) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn('[QA compacto] No se pudo parsear JSON de entorno.', error);
    return null;
  }
}

function getRectHeight(target) {
  if (!target) return 0;
  return Math.round(target.getBoundingClientRect().height);
}

function measureVisibleRows(rows, scrollContainer) {
  if (!rows.length) return 0;

  const frame = scrollContainer?.getBoundingClientRect?.() || {
    top: 0,
    bottom: window.innerHeight,
  };

  return rows.filter((row) => {
    const rect = row.getBoundingClientRect();
    const visibleTop = Math.max(rect.top, frame.top);
    const visibleBottom = Math.min(rect.bottom, frame.bottom, window.innerHeight);
    return visibleBottom - visibleTop >= Math.min(rect.height * 0.6, 24);
  }).length;
}

function formatMarkdown(report) {
  return [
    '| Pantalla | Viewport | Topbar | Filtros | Header/resultados | Filas visibles | Observaciones |',
    '| --- | --- | ---: | ---: | ---: | ---: | --- |',
    `| ${report.screen} | ${report.viewport.width}x${report.viewport.height} | ${report.heights.topbar}px | ${report.heights.filters}px | ${report.heights.resultsHeader}px | ${report.visibleRows} | ${report.observations.join(' · ') || '-'} |`,
  ].join('\n');
}

function buildReport({ screen, observations = [] }) {
  const root = document.querySelector('[data-qa-screen]');
  const topbar = document.querySelector('[data-qa-topbar]');
  const filters = document.querySelector('[data-qa-filters]');
  const resultsHeader = document.querySelector('[data-qa-results-header]');
  const scrollContainer = document.querySelector('[data-qa-results-scroll]') || root;
  const rows = Array.from(document.querySelectorAll('[data-qa-row]'));
  const report = {
    screen,
    route: window.location.pathname,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    heights: {
      topbar: getRectHeight(topbar),
      filters: getRectHeight(filters),
      resultsHeader: getRectHeight(resultsHeader),
    },
    visibleRows: measureVisibleRows(rows, scrollContainer),
    observations,
  };

  return {
    ...report,
    markdown: formatMarkdown(report),
  };
}

function scheduleMeasure(screen, observations) {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      const report = buildReport({ screen, observations });
      const store = (window.__operatorCompactQA = window.__operatorCompactQA || {
        history: [],
      });

      store.last = report;
      store.history = [
        ...store.history.filter((item) => item.route !== report.route),
        report,
      ];
      store.measure = () => buildReport({ screen, observations });
      store.printMarkdown = () =>
        (store.history.length ? store.history : [report]).map((item) => item.markdown).join('\n');

      console.groupCollapsed(
        `[QA compacto] ${report.screen}: ${report.visibleRows} fila(s) visibles en ${report.viewport.width}x${report.viewport.height}`,
      );
      console.table([
        {
          pantalla: report.screen,
          viewport: `${report.viewport.width}x${report.viewport.height}`,
          topbar: report.heights.topbar,
          filtros: report.heights.filters,
          header_resultados: report.heights.resultsHeader,
          filas_visibles: report.visibleRows,
          observaciones: report.observations.join(' | ') || '-',
        },
      ]);
      console.log(report.markdown);
      console.groupEnd();
    });
  });
}

export function bootstrapOperatorCompactQaSession() {
  if (!QA_SESSION_ENABLED) return;

  const payload = parseJsonEnv(import.meta.env.VITE_QA_SESSION_JSON);
  if (!payload?.token || !payload?.user) {
    console.warn('[QA compacto] VITE_QA_SESSION_JSON no contiene token/user.');
    return;
  }

  syncStoredSession({
    token: payload.token,
    user: payload.user,
    tenant: payload.tenant || null,
  });
}

export function useOperatorCompactQaScreen(screen, observations = []) {
  const observationsKey = observations.join(' | ');
  const normalizedObservations = observationsKey ? observationsKey.split(' | ') : [];

  useEffect(() => {
    if (!QA_ENABLED) return;
    scheduleMeasure(screen, normalizedObservations);

    const handleResize = () => scheduleMeasure(screen, normalizedObservations);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [screen, observationsKey]);
}
